// Module: FlowScreen
// Role: Root orchestrator for /flow/:slotId route. Selects template based on
//       flow state machine (pending/rejected/accepted/completed).
// Uses: flow-client, templates, PlatformProvider (back btn, capabilities)
// Used by: App.tsx route /flow/:slotId
//
// MOCK STRATEGY (L-220): VITE_FLOW_MOCK constant-folded by Vite; mock module
// tree-shaken in prod (sideEffects: false assumed).

import { useContext, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSafeBack } from '../../hooks/useSafeBack';
import * as realClient from '../../api/flow-client';
import * as mockClient from '../../api/flow-client.mock';
import type { GetFlowResponse, CheckpointAttempt } from '../../api/flow-types';
import { InstructionScreen } from './templates/InstructionScreen';
import { BriefScreen } from './templates/BriefScreen';
import { AwaitingVerdict } from './templates/AwaitingVerdict';
import { RejectScreen } from './templates/RejectScreen';
import { TerminalSheet } from './templates/TerminalSheet';
import { ModerationStatus } from './templates/ModerationStatus';
import { CartScreen } from './templates/CartScreen';
import { S04SuccessContinue } from './templates/S04SuccessContinue';
import { VideoVerdictScreen } from './templates/VideoVerdictScreen';
import { PickupOverviewScreen } from './templates/PickupOverviewScreen';
import { UgcOverviewScreen } from './templates/UgcOverviewScreen';
import { PublishOverviewScreen } from './templates/PublishOverviewScreen';
import { WaitScreen } from './templates/WaitScreen';
import { UgcTextScreen } from './templates/UgcTextScreen';
import { UgcPhotoGridScreen } from './templates/UgcPhotoGridScreen';
import { PlatformContext } from '../../providers/PlatformProvider';
import { clearPendingDeeplink } from '../onboarding/store';
import { bricksForCheckpoint } from './bricks/brick-registry';
import { submitErrorText } from './submitErrorText';
import { cancelSlot } from '../../api/slots';
import { StaticPage, Card, PressBtn } from '../../components/atoms';
import { G, P, S, T } from '../../styles/tokens';

const isMock = import.meta.env.VITE_FLOW_MOCK === 'true';
const { useFlow, useSubmitCheckpoint, useDevAcceptCheckpoint } = isMock ? mockClient : realClient;

function findLatestAttempt(data: GetFlowResponse, cpId: string): CheckpointAttempt | undefined {
  const forCp = data.attempts.filter((a) => a.checkpoint_id === cpId);
  if (forCp.length === 0) return undefined;
  return [...forCp].sort((a, b) => b.attempt_number - a.attempt_number)[0];
}

/** CTA label for TG MainButton mirroring in-page CtaBar. null = no CTA visible. */
function deriveCTALabel(data: GetFlowResponse | undefined, verdict: string | undefined): string | null {
  if (!data) return null;
  if (data.flow_run.status === 'completed') return 'На главную';
  if (!data.next_checkpoint) return null;
  if (verdict === 'pending') return null;
  // Rejected/escalated: no MainButton (retry is owned by template CTA).
  if (verdict === 'rejected') return null;
  if (verdict === 'escalated_to_moderation') return null;
  // Fresh data-entry: template owns its own CTA. Only accepted is mirrored.
  if (verdict !== 'accepted') return null;
  const kind = data.next_checkpoint.kind;
  if (kind === 'MODERATION') return null;
  if (kind === 'WAIT') return null; // FTR-1224: WAIT CTA owned by in-page WizardCtaBar, not TG MainButton (SA-4)
  if (kind === 'PUBLISH') return 'Опубликовать';
  return 'Продолжить';
}

/** FTR-1195: brief intro card shown before first work step. */
function BriefIntro({ brief }: { brief: string | null | undefined }) {
  if (!brief) return null;
  return (
    <div
      style={{
        margin: '0 16px 12px',
        padding: '14px 16px',
        borderRadius: 16,
        background: P.terraSoft,
        border: `1px solid rgba(226,114,91,0.15)`,
      }}
    >
      <div style={{ font: T.caption, color: P.terracotta, fontWeight: 600, marginBottom: 6 }}>
        Бриф задания
      </div>
      <div style={{ font: T.footnote, color: S.label, lineHeight: 1.45, whiteSpace: 'pre-wrap' }}>
        {brief}
      </div>
    </div>
  );
}

/** Render an honest inline error banner for 409/429 submit errors (EC-6, EC-7). */
function SubmitErrorBanner({ error }: { error: Error | null }) {
  if (!error) return null;
  const text = submitErrorText(error);
  return (
    <div
      style={{
        position: 'fixed',
        bottom: 80,
        left: 16,
        right: 16,
        padding: '12px 16px',
        borderRadius: 14,
        ...G.errorTag,
        zIndex: 50,
      }}
    >
      <p style={{ font: T.footnote, color: P.danger, margin: 0, textAlign: 'center' }}>{text}</p>
    </div>
  );
}

/** BUG-1309: Bottom-sheet confirm dialog for slot cancellation. */
function CancelConfirmSheet({ onConfirm, onClose, isLoading }: {
  onConfirm: () => void;
  onClose: () => void;
  isLoading: boolean;
}) {
  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '100%', maxWidth: 480, background: P.bg,
          borderRadius: '20px 20px 0 0', padding: '24px 20px 32px',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ font: T.title3, color: S.label, marginBottom: 8, textAlign: 'center' }}>
          Отказаться от задания?
        </div>
        <div style={{ font: T.footnote, color: P.muted, marginBottom: 20, textAlign: 'center', lineHeight: 1.45 }}>
          Слот вернётся в общий пул. Вы сможете взять другое задание.
        </div>
        <PressBtn
          onClick={onConfirm}
          style={{
            width: '100%', marginBottom: 10,
            background: P.danger, color: '#fff',
            opacity: isLoading ? 0.6 : 1,
          }}
          disabled={isLoading}
        >
          {isLoading ? 'Отменяю...' : 'Отказаться'}
        </PressBtn>
        <PressBtn
          onClick={onClose}
          style={{
            width: '100%',
            background: 'transparent', color: S.label,
            border: `1px solid ${P.muted}33`,
          }}
        >
          Назад
        </PressBtn>
      </div>
    </div>
  );
}

/** BUG-1309: check if buyer can cancel -- zero accepted attempts, non-terminal flow. */
function canCancelSlot(data: GetFlowResponse): boolean {
  if (data.flow_run.status === 'completed') return false;
  if (data.flow_run.status === 'cancelled') return false;
  if (data.flow_run.status === 'expired') return false;
  // BUG-1309 follow-up: mirror the backend cancel guard (src/api/v2/buyer/slots.py:472-492),
  // which blocks cancel when the slot is pending_review (moderation) or has any
  // accepted / in-flight(pending) checkpoint attempt. On the FE, an attempt with
  // verdict 'escalated_to_moderation' is the signal that the slot has entered
  // pending_review. Hide the CTA in all three cases so the UI matches the server
  // (which would otherwise respond 409). Eligible slots are unaffected.
  return !data.attempts.some(
    (a) =>
      a.verdict === 'accepted' ||
      a.verdict === 'pending' ||
      a.verdict === 'escalated_to_moderation',
  );
}

export default function FlowScreen() {
  const { slotId } = useParams<{ slotId: string }>();
  const navigate = useNavigate();
  const safeBack = useSafeBack();
  const { data, isLoading, error } = useFlow(slotId ?? '');

  const cp = data?.next_checkpoint ?? null;
  const latestAttempt = cp ? findLatestAttempt(data!, cp.id) : undefined;
  const verdict = latestAttempt?.verdict;
  const ctaHandlerRef = useRef<(() => void) | null>(null);
  const terminalSubmitRef = useRef<string | null>(null);

  // Submit hook — unconditional (hooks must not be conditional).
  const submitCheckpoint = useSubmitCheckpoint(slotId ?? '', cp?.id ?? '');
  const devAcceptCheckpoint = useDevAcceptCheckpoint(slotId ?? '', cp?.id ?? '');
  const submitError = submitCheckpoint.error ?? null;
  const devAcceptError = devAcceptCheckpoint.error ?? null;

  // Rejected-retry: reset when attempt changes so RejectScreen shows first.
  const [retrying, setRetrying] = useState(false);
  const latestAttemptId = latestAttempt?.id;
  useEffect(() => { setRetrying(false); }, [latestAttemptId]);

  // BUG-1309: cancel sheet state
  const [showCancelSheet, setShowCancelSheet] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  const handleCancelConfirm = async () => {
    if (!slotId || cancelLoading) return;
    setCancelLoading(true);
    try {
      await cancelSlot(slotId);
      navigate('/', { replace: true });
    } catch {
      setCancelLoading(false);
      // Error: stay on sheet so buyer can retry or close
    }
  };

  // Back button: null-safe so tests without PlatformProvider render without throwing.
  const platform = useContext(PlatformContext);
  useEffect(() => {
    if (!platform) return;
    platform.showBackButton(safeBack);
    return () => { platform.hideBackButton(); };
  }, [platform, safeBack]);

  // Haptics on verdict transition to rejected/accepted.
  useEffect(() => {
    if (verdict === 'rejected' || verdict === 'accepted') {
      platform?.hapticImpact('medium');
    }
  }, [verdict, platform]);

  // TG MainButton: mirror in-page CTA on Telegram platform only.
  const ctaLabel = deriveCTALabel(data, verdict);
  const nativeMainButton = platform?.capabilities.nativeBackButton ?? false;
  useEffect(() => {
    if (!platform || !nativeMainButton) return;
    if (!ctaLabel) { platform.hideMainButton(); return; }
    const handler = () => ctaHandlerRef.current?.();
    platform.setMainButton(ctaLabel, handler);
    return () => { platform.hideMainButton(); };
  }, [ctaLabel, nativeMainButton, platform]);

  const terminalDoneKey =
    data && cp && data.flow_run.status !== 'completed' && cp.kind === 'DONE'
      ? `${data.flow_run.id}:${cp.id}`
      : null;
  useEffect(() => {
    if (!terminalDoneKey) return;
    if (terminalSubmitRef.current === terminalDoneKey) return;
    terminalSubmitRef.current = terminalDoneKey;
    submitCheckpoint.mutate({ artefact_path: '' });
  }, [terminalDoneKey, submitCheckpoint]);

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          minHeight: '100dvh', background: P.bg,
        }}
      >
        <div
          style={{
            width: 48, height: 48, borderRadius: 24,
            border: `4px solid rgba(226,114,91,0.18)`,
            borderTopColor: P.terracotta,
            animation: 'spin 1s linear infinite',
          }}
        />
        <span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0,0,0,0)' }}>
          Загрузка...
        </span>
      </div>
    );
  }

  if (error || !data) {
    // BUG-1108: distinguish FLOW_NOT_FOUND (legacy slot, expected) from real errors.
    const status = (error as (Error & { status?: number }) | null)?.status;
    const msg = (error as Error | null)?.message ?? '';
    const isFlowNotFound = status === 404 && msg.includes('FLOW_NOT_FOUND');
    const text = isFlowNotFound
      ? 'Задание взято. Это задание выполняется в основном боте — вернись на главную.'
      : 'Не удалось загрузить задание. Попробуй открыть его заново или вернись на главную.';
    return (
      <StaticPage title="Задание" onBack={safeBack}>
        <Card p={16} r={20}>
          <p style={{ margin: 0 }}>{text}</p>
          <div style={{ marginTop: 12 }}>
            <PressBtn onClick={() => navigate('/', { replace: true })}>
              На главную
            </PressBtn>
          </div>
        </Card>
      </StaticPage>
    );
  }

  // --- State machine ---

  /** Real onCta: submits the active checkpoint attempt (EC-5). */
  const onCta = () => {
    submitCheckpoint.mutate({ artefact_path: '' });
  };
  const onDevAccept = () => {
    devAcceptCheckpoint.mutate();
  };
  ctaHandlerRef.current = onCta;

  const renderTerminalDone = () => {
    const fr = data.flow_run as unknown as Record<string, unknown>;
    const cashback = fr.cashback_amount_kopecks;
    const payFirst = fr.payout_first_kopecks;
    const paySecond = fr.payout_second_kopecks;
    const handleDone = () => navigate('/');
    ctaHandlerRef.current = handleDone;

    // Split-payout variant when both payout fields are present.
    if (typeof payFirst === 'number' && typeof paySecond === 'number') {
      return (
        <TerminalSheet
          variant="done-split"
          payoutFirst={Math.round(payFirst / 100)}
          payoutSecond={Math.round(paySecond / 100)}
          onCta={handleDone}
        />
      );
    }

    return (
      <TerminalSheet
        variant="done"
        cashbackAmount={typeof cashback === 'number' ? Math.round(cashback / 100) : undefined}
        onCta={handleDone}
      />
    );
  };

  // 1. Completed flow → terminal done screen (or done-split for split-payout campaigns)
  if (data.flow_run.status === 'completed') {
    return renderTerminalDone();
  }

  // 2. No next checkpoint on non-completed run → data error
  if (!data.next_checkpoint) {
    return (
      <div
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          minHeight: '100dvh', background: P.bg,
        }}
      >
        <p style={{ font: T.footnote, color: P.muted, padding: '0 16px', textAlign: 'center', margin: 0 }}>
          Нет активных заданий
        </p>
      </div>
    );
  }

  const cpResolved = data.next_checkpoint;

  // DONE is technical terminal checkpoint. Buyer sees the pretty terminal screen, not a 17th form.
  if (cpResolved.kind === 'DONE') {
    return renderTerminalDone();
  }

  const commonProps = {
    checkpoint: cpResolved,
    attempt: latestAttempt,
    flowRun: data.flow_run,
    checkpoints: data.checkpoints,
    attempts: data.attempts,
    onCta,
  };

  // Editor for active checkpoint (fresh data-entry or rejected-retry).
  const renderCheckpointEditor = () => {
    switch (cpResolved.kind) {
      case 'CART':
        return (
          <>
            <CartScreen {...commonProps} />
            <SubmitErrorBanner error={submitError} />
          </>
        );
      case 'PICKUP':
        return <PickupOverviewScreen {...commonProps} />;
      case 'UGC':
        return <UgcOverviewScreen {...commonProps} />;
      case 'UGC_TEXT':
        return <UgcTextScreen {...commonProps} />;
      case 'UGC_PHOTO':
        return (
          <>
            <UgcPhotoGridScreen {...commonProps} />
            <SubmitErrorBanner error={submitError} />
          </>
        );
      case 'MODERATION':
        // BUG-1223: inject onLeaveHome so buyer can escape while waiting for auto-moderation
        return <ModerationStatus {...commonProps} onLeaveHome={() => { clearPendingDeeplink(); navigate('/', { replace: true }); }} />;
      case 'PUBLISH': {
        // FTR-1207: show overview when multiple PUBLISH steps exist in the flow.
        const publishSiblings = data.checkpoints.filter((c) => c.kind === 'PUBLISH');
        if (publishSiblings.length > 1) {
          return <PublishOverviewScreen {...commonProps} />;
        }
        // Single PUBLISH: fall through to brick dispatch.
        const pubBricks = bricksForCheckpoint(cpResolved);
        if (pubBricks.length > 0) {
          const { key, Component } = pubBricks[0];
          return (
            <>
              <Component key={key} {...commonProps} />
              <SubmitErrorBanner error={submitError} />
            </>
          );
        }
        return (
          <>
            <InstructionScreen {...commonProps} />
            <SubmitErrorBanner error={submitError} />
          </>
        );
      }
      case 'PHOTO':
      case 'VIDEO_UPLOAD': {
        // Brick dispatch: resolve bricks from artefact_requirements.
        const bricks = bricksForCheckpoint(cpResolved);
        if (bricks.length > 0) {
          const { key, Component } = bricks[0];
          return (
            <>
              <Component key={key} {...commonProps} />
              <SubmitErrorBanner error={submitError} />
            </>
          );
        }
        // Fallback: no artefact_requirements -> InstructionScreen (WAIT-like steps)
        return (
          <>
            <InstructionScreen {...commonProps} />
            <SubmitErrorBanner error={submitError} />
          </>
        );
      }
      case 'BRIEF':
        return (
          <>
            <BriefScreen {...commonProps} />
            <SubmitErrorBanner error={submitError} />
          </>
        );
      case 'WAIT':
        // L-250: WAIT determined by kind + flow_run.status, NOT date.
        // FTR-1224: wrap SubmitErrorBanner so buyer sees 409/429/network on self-advance tap.
        return (
          <>
            <WaitScreen {...commonProps} />
            <SubmitErrorBanner error={submitError} />
          </>
        );
      default:
        return (
          <>
            <InstructionScreen {...commonProps} />
            <SubmitErrorBanner error={submitError} />
          </>
        );
    }
  };

  // 3. Pending verdict → awaiting screen (no CTA)
  if (latestAttempt?.verdict === 'pending') return <AwaitingVerdict {...commonProps} />;

  // 4. Rejected → reasons screen or VideoVerdictScreen for VIDEO_UPLOAD.
  if (latestAttempt?.verdict === 'rejected') {
    if (retrying) return renderCheckpointEditor();
    // FTR-1206: VIDEO_UPLOAD → VideoVerdictScreen (soft/hard fail branches)
    if (cpResolved.kind === 'VIDEO_UPLOAD') {
      return (
        <VideoVerdictScreen
          checkpoint={cpResolved}
          attempt={latestAttempt}
          flowRun={data.flow_run}
          checkpoints={data.checkpoints}
          onCta={onCta}
          onRetry={() => setRetrying(true)}
        />
      );
    }
    return (
      <>
        <RejectScreen {...commonProps} onCta={() => setRetrying(true)} />
        <SubmitErrorBanner error={submitError} />
      </>
    );
  }

  // 4b. Escalated to moderation
  if (latestAttempt?.verdict === 'escalated_to_moderation') {
    // FTR-1206: VIDEO_UPLOAD → VideoVerdictScreen (escalated branch)
    if (cpResolved.kind === 'VIDEO_UPLOAD') {
      return (
        <VideoVerdictScreen
          checkpoint={cpResolved}
          attempt={latestAttempt}
          flowRun={data.flow_run}
          checkpoints={data.checkpoints}
          onCta={onCta}
          onRetry={() => setRetrying(true)}
        />
      );
    }
    // BUG-1223: manual=true shows honest copy (no countdown); onLeaveHome lets buyer escape
    return (
      <>
        <ModerationStatus
          {...commonProps}
          onCta={onDevAccept}
          manual
          onLeaveHome={() => { clearPendingDeeplink(); navigate('/', { replace: true }); }}
        />
        <SubmitErrorBanner error={devAcceptError} />
      </>
    );
  }

  // 4a. Accepted → VideoVerdictScreen for VIDEO_UPLOAD, else S04SuccessContinue.
  if (latestAttempt?.verdict === 'accepted') {
    if (cpResolved.kind === 'VIDEO_UPLOAD') {
      return (
        <VideoVerdictScreen
          checkpoint={cpResolved}
          attempt={latestAttempt}
          flowRun={data.flow_run}
          checkpoints={data.checkpoints}
          onCta={onCta}
          onRetry={() => setRetrying(true)}
        />
      );
    }
    const nextInSequence = data.checkpoints
      .filter((c) => c.position === cpResolved.position + 1)
      .sort((a, b) => a.position - b.position)[0] ?? null;
    // Top-level step count — matches the «X из N» denominator on every other
    // flow screen (was data.checkpoints.length, which counted child checkpoints).
    const parentCount = data.checkpoints.filter(
      (c) => c.parent_checkpoint_id === null,
    ).length;
    return (
      <S04SuccessContinue
        acceptedCheckpoint={cpResolved}
        totalCheckpoints={parentCount}
        nextCheckpoint={nextInSequence}
        onCta={onCta}
      />
    );
  }

  // 5. Fresh data-entry → render the checkpoint editor.
  // FTR-1195: show brief intro before first work step (position 0).
  // BUG-1309: cancel CTA below checkpoint editor, gated by canCancelSlot.
  const showCancel = canCancelSlot(data);
  const cancelCta = showCancel ? (
    <div style={{ padding: '12px 16px 24px', textAlign: 'center' }}>
      <button
        onClick={() => setShowCancelSheet(true)}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          font: T.footnote, color: P.muted, textDecoration: 'underline',
          padding: '8px 0',
        }}
      >
        Отказаться от задания
      </button>
    </div>
  ) : null;

  const cancelSheet = showCancelSheet ? (
    <CancelConfirmSheet
      onConfirm={() => { void handleCancelConfirm(); }}
      onClose={() => setShowCancelSheet(false)}
      isLoading={cancelLoading}
    />
  ) : null;

  if (data.brief_content && cpResolved.position === 0 && cpResolved.kind !== 'BRIEF') {
    return (
      <>
        <BriefIntro brief={data.brief_content} />
        {renderCheckpointEditor()}
        {cancelCta}
        {cancelSheet}
      </>
    );
  }
  return (
    <>
      {renderCheckpointEditor()}
      {cancelCta}
      {cancelSheet}
    </>
  );
}
